import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Link } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { ReactComponent as ExcelIcon } from "assets/excel.svg";
import Button from "components/common/Button";
import DialogFormation from "components/common/DialogFormation";
import Input from "components/common/Input";
import Typography from "components/common/Typography";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { onDownloadClick } from "utils/CommonFunctions";
import { setShowImportDialog } from "./sme.slice";

interface Props {
  onSubmit: (args?: any) => void;
}

const ImportDialog = (props: Props) => {
  const { onSubmit } = props;
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<FileList | null>(null);
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
  const { showImportDialog } = useAppSelector((state) => state.sme);
  const {
    register,
    reset,
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  const handleClose = () => {
    setFile(null);
    reset();
    dispatch(setShowImportDialog(false));
  };

  const handleFileChange = (e: any) => {
    const fileExtension = e.target.files[0].name.split(".")[1];

    if (fileExtension !== "xlsx") {
      setError("importFile", { type: "validate", message: "Invalid file" });
      return;
    }

    setFile(e.target.files);
    setValue("importFile", e.target.files);
    clearErrors("importFile");
  };

  const handleRemoveFile = () => {
    setFile(null);
    setValue("importFile", "");
  };

  useEffect(() => {
    if (!showImportDialog) {
      setFile(null);
      reset();
    }
  }, [showImportDialog]);

  const watchListName = watch("listName");

  useEffect(() => {
    if (Object.keys(errors).length > 0 || watchListName?.trim().length < 1 || !file) {
      setBtnDisabled(true);
    } else {
      setBtnDisabled(false);
    }
  }, [file, watchListName, errors]);

  return (
    <DialogFormation
      openDialog={showImportDialog}
      handleClose={handleClose}
      title="Import Inncode List"
      actionConfig={
        <div className="modal-config-wrapper">
          <Button disabled={btnDisabled} label="Import List" onClick={handleSubmit(onSubmit)} />
        </div>
      }
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} sx={{ textAlign: "left" }}>
        <Input
          label="List Name"
          name="listName"
          register={register}
          error={errors?.listName}
          validations={{ required: true }}
        />
        <Typography
          variant="body1"
          showBold
          color={tokens.Text.General.PrimaryHeadline}
          sx={{
            marginTop: "24px",
            marginBottom: "12px"
          }}
        >
          Import File
        </Typography>
        <Box mb={3}>
          <Typography variant="body2" color={tokens.Text.General.PrimaryBody} sx={{ marginBottom: "12px" }}>
            Use the template from the link to create your Inncode list.
          </Typography>
          <Link type="button" component="button" onClick={() => onDownloadClick("inncode-groups/template", "userplan")}>
            Download Template
          </Link>
        </Box>
        <Typography variant="body2" color={tokens.Text.General.PrimaryBody} sx={{ marginBottom: "12px" }}>
          Use the button below to upload your <b>Excel File</b> with the Inncode list
        </Typography>
        <Box sx={{ position: "relative" }}>
          {file?.length ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                height: "76px",
                backgroundColor: tokens.Background.General.Secondary,
                borderRadius: "8px",
                padding: "18px 10px 18px 18px"
              }}
            >
              <ExcelIcon />
              <Box sx={{ flex: 1, marginLeft: "12px" }}>
                <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
                  {file?.length ? file[0]?.name : ""}
                </Typography>
                <Typography variant="label" showBold color={tokens.Text.General.Secondary} sx={{ fontSize: "12px" }}>
                  {file?.length ? `${(file[0]?.size / 1024 ** 2).toFixed(2)} MB` : ""}
                </Typography>
              </Box>
              <IconButton aria-label="close" onClick={handleRemoveFile} sx={{ color: tokens.Icon.General.Primary }}>
                <CloseIcon sx={{ fontSize: "18px" }} />
              </IconButton>
            </Stack>
          ) : (
            <>
              <Box
                component="input"
                type="file"
                accept=".xlsx"
                sx={{
                  position: "absolute",
                  zIndex: 1,
                  height: "100%",
                  width: "100%",
                  borderRadius: "200px",
                  cursor: "pointer",
                  opacity: 0
                }}
                onChange={handleFileChange}
              />
              <Button
                variant="secondary"
                startIcon={<UploadFileIcon />}
                label={file?.length ? file[0]?.name : "Upload Excel File"}
                width="100%"
              />
            </>
          )}
        </Box>
        {errors?.importFile && (
          <Typography variant="label" color={tokens.Text.Feedback.Alert}>
            {errors?.importFile?.message ?? "This field is required to be completed. "}
          </Typography>
        )}
      </Stack>
    </DialogFormation>
  );
};

export default ImportDialog;
